import { SECURITY_KEY, BASE_API_URL, BASE_restAPI_URL } from "../../globals";

/**
 * Authentication and OTP Utilities
 */

// Normalize mobile number: strips +91, 91, 0, spaces, and dashes, keeping last 10 digits
export const normalizeMobile = (input) => {
  if (!input) return "";
  const cleaned = String(input).trim().replace(/[\s\-()+]/g, "");
  if (cleaned.length > 10) {
    return cleaned.slice(-10);
  }
  return cleaned;
};

// Generate a secure 4-digit random OTP
export const generateSecureOTP = () => {
  try {
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
      const buffer = new Uint32Array(1);
      window.crypto.getRandomValues(buffer);
      // Generates integer between 1000 and 9999
      return String(1000 + (buffer[0] % 9000));
    }
  } catch (err) {
    console.warn("crypto.getRandomValues fallback:", err);
  }
  return String(Math.floor(1000 + Math.random() * 9000));
};

// Format seconds into MM:SS
export const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * 1. Check if registered mobile number exists in masterdata
 * Handles numbers with or without country code (+91)
 */
export const checkMobileExists = async (mobile) => {
  const clean = normalizeMobile(mobile);
  if (!clean || clean.length !== 10) {
    return {
      success: false,
      exists: false,
      message: "Please enter a valid 10-digit mobile number.",
    };
  }

  try {
    const data = new URLSearchParams();
    data.append("SecurityKey", SECURITY_KEY);
    data.append("TableName", "masterdata");
    data.append("WhereCondition", `MobileNo='${clean}' OR MobileNo='+91${clean}' OR MobileNo='91${clean}' OR MobileNo LIKE '%${clean}%'`);
    data.append("*", "*");

    const res = await fetch(`${BASE_restAPI_URL}Selectdata.aspx`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    });

    const json = await res.json();
    const list = json?.Response;

    if (Array.isArray(list) && list.length > 0) {
      // Find the user matching the normalized 10-digit number
      const foundUser = list.find((u) => {
        const uNum = normalizeMobile(u.MobileNo || u.mobileno || u.Mobile);
        return uNum === clean;
      }) || list[0];

      return {
        success: true,
        exists: true,
        user: foundUser,
      };
    }

    // Fallback: Check with WhereCondition All if specific filter didn't match
    const fallbackData = new URLSearchParams();
    fallbackData.append("SecurityKey", SECURITY_KEY);
    fallbackData.append("TableName", "masterdata");
    fallbackData.append("WhereCondition", "All");
    fallbackData.append("*", "*");

    const fallbackRes = await fetch(`${BASE_restAPI_URL}Selectdata.aspx`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: fallbackData.toString(),
    });

    const fallbackJson = await fallbackRes.json();
    const allUsers = fallbackJson?.Response;

    if (Array.isArray(allUsers) && allUsers.length > 0) {
      const matched = allUsers.find((u) => {
        const uNum = normalizeMobile(u.MobileNo || u.mobileno || u.Mobile);
        return uNum === clean;
      });

      if (matched) {
        return {
          success: true,
          exists: true,
          user: matched,
        };
      }
    }

    return {
      success: false,
      exists: false,
      message: "Mobile number is not registered. Please check and try again.",
    };
  } catch (error) {
    console.error("Check mobile error:", error);
    return {
      success: false,
      exists: false,
      message: "Unable to verify mobile number. Please check connection.",
    };
  }
};

/**
 * 2. Send 4-digit OTP via WhatsApp API
 * Domain: Attendance.riww.in/RestApi/WhatsappMsg.aspx
 */
export const sendWhatsappOtp = async (mobile, otp) => {
  const clean = normalizeMobile(mobile);

  try {
    const fd = new FormData();
    fd.append("Action", "Forgot");
    fd.append("Token", "ffLpaHkJOoXi4j8");
    fd.append("Mobile", clean);
    fd.append("OTP", String(otp));

    // mode: 'no-cors' allows browser to send the POST to Attendance.riww.in without throwing CORS error
    await fetch("https://Attendance.riww.in/RestApi/WhatsappMsg.aspx", {
      method: "POST",
      mode: "no-cors",
      body: fd,
    });

    return {
      success: true,
      message: "OTP sent successfully to your WhatsApp.",
    };
  } catch (error) {
    console.error("WhatsappMsg API call error:", error);
    return {
      success: false,
      message: "Network error sending OTP. Please check your internet connection.",
    };
  }
};

/**
 * 3. Reset Password API
 * Resets password identically to Profile (using profile change-password and updatedata.aspx)
 */
export const resetUserPassword = async ({ userId, newPassword, confirmPassword, mobile, user }) => {
  const targetId = userId || user?.ID || user?.id;

  if (!targetId) {
    return {
      success: false,
      message: "User ID not found. Please restart the process.",
    };
  }

  let updateSuccess = false;
  let lastError = "";

  // 1. Same as Profile: Call profile/change-password
  try {
    const apiBase = (process.env.REACT_APP_BASE_API_URL || `${BASE_API_URL}api`).replace(/\/+$/, "");
    const profileChangeUrl = `${apiBase}/profile/change-password`;
    const res = await fetch(profileChangeUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: targetId,
        newPassword,
        confirmPassword: confirmPassword || newPassword,
      }),
    });

    const data = await res.json();
    if (res.ok && data.success) {
      updateSuccess = true;
    } else if (data.error) {
      lastError = data.error;
    }
  } catch (err) {
    console.warn("profile/change-password call notice:", err.message);
  }

  // 2. Also update via updatedata.aspx (same as Master Resetpass)
  try {
    const updateData = new URLSearchParams({
      SecurityKey: SECURITY_KEY,
      TableName: "masterdata",
      WhereCondition: `ID=${targetId}`,
      Password: newPassword,
    });

    const aspxRes = await fetch(`${BASE_restAPI_URL}updatedata.aspx`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: updateData.toString(),
    });

    const text = await aspxRes.text();
    let aspxJson = null;
    try {
      aspxJson = JSON.parse(text);
    } catch {
      // Plain text response from legacy backend
      updateSuccess = true;
    }

    if (aspxJson?.Response?.[0]?.Status === "Ok" || !aspxJson?.error) {
      updateSuccess = true;
    }
  } catch (err) {
    console.warn("updatedata.aspx call notice:", err.message);
  }

  if (updateSuccess) {
    return {
      success: true,
      message: "Password updated successfully",
    };
  }

  return {
    success: false,
    message: lastError || "Failed to update password. Please try again.",
  };
};
